import { Button } from '@base-ui/react/button';
import {
  faComments,
  faListCheck,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import { apiErrorDetail } from '@/api/errorDetail';
import type { components } from '@/api/qraft/schema';
import { ConfirmDialog } from '~/components/ui/ConfirmDialog';
import {
  DangerGroup,
  EditorLayout,
  EditorMain,
  EditorPanel,
  FormSection,
  PanelGroup,
  SaveGroup,
} from '~/components/ui/Editor';
import {
  CheckboxLabel,
  ErrorNotice,
  ErrorText,
  FieldHint,
  FieldPair,
  FormLabel,
  FormRow,
  InfoNotice,
  Select,
  TextInput,
  WarningNotice,
} from '~/components/ui/Form';
import { QuizBadge } from '~/components/ui/QuizBadge';
import { RichContent } from '~/components/ui/RichContent';
import { RichTextEditor } from '~/components/ui/RichTextEditor';
import { useUnsavedChanges } from '~/components/ui/useUnsavedChanges';
import {
  deletePrompt,
  EditConflictNotice,
  editConflict,
} from '~/components/ui/Versions';
import buttonStyles from '~/styles/button.module.css';

type QuestionAdmin = components['schemas']['QuestionAdmin'];
type QuestionType = components['schemas']['QuestionTypes'];
type ContentStatus = components['schemas']['ContentStatus'];
type Answer = components['schemas']['Answer'];

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  choose_one: 'Single choice',
  choose_multiple: 'Multiple choice',
  fill_in_blank: 'Fill in the blank',
  essay: 'Essay',
};

// Stored blanks are LearnDash's marker. Authors write `{answer}` inline, the
// syntax LearnDash itself used, and the answers are read back out of the text.
export const CLOZE_MARKER = '@@wpProQuizCloze@@';
const BLANK_RE = /\{([^{}]*)\}/g;

const escapeHtml = (value: string): string =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');

const plainText = (html: string): string =>
  new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';

// The editor returns an empty paragraph for an empty box.
const emptyToBlank = (html: string): string =>
  plainText(html).trim() === '' && !html.includes('<img') ? '' : html;

function clozeToEditable(replacer: string | null, answers: Answer[]): string {
  let i = 0;
  return (replacer ?? '').replaceAll(CLOZE_MARKER, () => {
    const answer = answers[i++];
    const spellings = [answer?.text ?? '', ...(answer?.alternatives ?? [])];
    return spellings.length > 1
      ? `{${spellings.map((s) => `[${escapeHtml(s)}]`).join('')}}`
      : `{${escapeHtml(spellings[0])}}`;
  });
}

// `{answer}` or `{[answer][other spelling]}`; a `|points` suffix is ignored, and
// so is a repeat that differs only by case, since grading ignores case.
function parseBlank(inner: string): Answer {
  const text = plainText(inner);
  const options = [...text.matchAll(/\[([^\]]*)\]/g)].map((m) => m[1]);
  const spellings: string[] = [];
  for (const option of options.length ? options : [text]) {
    const spelling = option.replace(/\|\d+\s*$/, '').trim();
    const seen = spellings.some(
      (s) => s.toLowerCase() === spelling.toLowerCase(),
    );
    if (spelling && !seen) spellings.push(spelling);
  }
  return {
    text: spellings[0] ?? '',
    is_correct: true,
    alternatives: spellings.slice(1),
  };
}

function parseCloze(html: string): { replacer: string; answers: Answer[] } {
  const answers: Answer[] = [];
  const replacer = html.replace(BLANK_RE, (_, inner: string) => {
    answers.push(parseBlank(inner));
    return CLOZE_MARKER;
  });
  return { replacer, answers };
}

const acceptedList = (answer: Answer): string[] => [
  answer.text,
  ...(answer.alternatives ?? []),
];

// "63 AD / 63": every spelling a blank accepts.
export const acceptedSpellings = (answer: Answer): string =>
  acceptedList(answer).join(' / ');

// Rendered question body with each blank shown as a gap or as its answer.
export function clozeHtml(
  replacer: string | null,
  answers: string[],
  show: 'answers' | 'gaps',
): string {
  let i = 0;
  return (replacer ?? '').replaceAll(CLOZE_MARKER, () => {
    const answer = answers[i++] ?? '';
    return show === 'answers'
      ? `<span class="blank">${escapeHtml(answer)}</span>`
      : '<span class="blank">&nbsp;</span>';
  });
}

const isChoiceType = (type: QuestionType): boolean =>
  type === 'choose_one' || type === 'choose_multiple';

const blankOptions = (): Answer[] => [
  { text: '', is_correct: false },
  { text: '', is_correct: false },
];

export function QuestionEditor({
  question,
  lessonId,
  topicId,
  quizLocked,
  onDone,
}: {
  // Absent when writing a new question.
  question?: QuestionAdmin;
  lessonId: number;
  topicId?: number;
  // Students may have reached the quiz: see `QuestionAdmin.quiz_locked`.
  quizLocked: boolean;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const creating = question === undefined;
  // Students may have it, so only its wording can change.
  const locked = question?.locked ?? false;
  // Devices holding the quiz would have no answer for a question added now.
  const parentName = topicId === undefined ? 'lesson' : 'topic';
  const initialType = question?.type ?? 'choose_one';

  const [type, setType] = useState<QuestionType>(initialType);
  // Pinned when editing starts: a refetch (e.g. on tab focus) must not advance it,
  // or a save over someone else's change would slip past the conflict check.
  const [baseRevision] = useState(question?.revision);
  const [text, setText] = useState(question?.text ?? '');
  const [clozeText, setClozeText] = useState(() =>
    question
      ? clozeToEditable(question.cloze_replacer, question.answers ?? [])
      : '',
  );
  // A blank question's answers are its blanks, all marked correct, so they
  // mustn't become options if the type is switched.
  const [answers, setAnswers] = useState<Answer[]>(
    isChoiceType(initialType) && question?.answers?.length
      ? question.answers
      : blankOptions(),
  );
  const [score, setScore] = useState(String(question?.score ?? 1));
  const [order, setOrder] = useState(String(question?.order ?? ''));
  const [status, setStatus] = useState<ContentStatus>(
    question?.status ?? 'draft',
  );
  const [hint, setHint] = useState(question?.hint ?? '');
  const [correctMsg, setCorrectMsg] = useState(question?.correct_msg ?? '');
  const [incorrectMsg, setIncorrectMsg] = useState(
    question?.incorrect_msg ?? '',
  );
  const [explanation, setExplanation] = useState(question?.explanation ?? '');
  const [disableCorrect, setDisableCorrect] = useState(
    question?.disable_correct ?? false,
  );
  const [triedToSave, setTriedToSave] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // `isPending` only disables Save after a re-render, so a fast double-click
  // could otherwise send two requests.
  const submitting = useRef(false);
  // Switching multiple to single choice keeps one correct option; switching
  // straight back restores the others.
  const singleChoiceTrim = useRef<{ before: Answer[]; after: Answer[] } | null>(
    null,
  );

  const isCloze = type === 'fill_in_blank';
  // Kept while another type is picked, but only choice questions send it.
  const noCorrect = isChoiceType(type) && disableCorrect;
  const cloze = parseCloze(clozeText);
  const submittedAnswers = isCloze
    ? cloze.answers
    : isChoiceType(type)
      ? answers
      : [];

  // Drafts may be saved half-written; these only block publishing.
  const publishing = status === 'published';
  const publishProblems: string[] = [];
  const lockProblems: string[] = [];
  const body = isCloze ? clozeText : text;
  if (publishing && plainText(body).trim() === '')
    publishProblems.push('Write the question.');
  // Existing questions are only held to the rules when their answer key
  // changes, so wording fixes on live migrated questions aren't blocked.
  const keyChanged =
    creating ||
    question.status !== 'published' ||
    type !== question.type ||
    noCorrect !== question.disable_correct ||
    JSON.stringify(submittedAnswers) !==
      JSON.stringify(question.answers ?? []) ||
    (isCloze && cloze.replacer !== (question.cloze_replacer ?? ''));
  if (publishing && keyChanged && isChoiceType(type)) {
    const correct = answers.filter((a) => a.is_correct).length;
    if (answers.length < 2) publishProblems.push('Add at least two options.');
    if (answers.some((a) => !a.text.trim()))
      publishProblems.push('Every option needs text.');
    if (!disableCorrect && type === 'choose_one' && correct !== 1)
      publishProblems.push(
        'Mark the correct option, or tick "No correct answer".',
      );
    if (!disableCorrect && type === 'choose_multiple' && correct < 1)
      publishProblems.push(
        'Mark at least one correct option, or tick "No correct answer".',
      );
  }
  // Once answered, a blank question may gain spellings but not lose any; its
  // earlier submissions keep the score they got.
  const addedSpellings: string[] = [];
  let removedSpelling = false;
  const originalBlanks = question?.answers ?? [];
  if (locked && isCloze && originalBlanks.length === cloze.answers.length) {
    const norm = (value: string) => value.trim().toLowerCase();
    originalBlanks.forEach((before, i) => {
      const after = acceptedList(cloze.answers[i]);
      const beforeSet = new Set(acceptedList(before).map(norm));
      const afterSet = new Set(after.map(norm));
      if ([...beforeSet].some((s) => !afterSet.has(s))) removedSpelling = true;
      addedSpellings.push(...after.filter((s) => !beforeSet.has(norm(s))));
    });
  }
  if (publishing && keyChanged && isCloze) {
    if (cloze.answers.length === 0)
      publishProblems.push(
        'Add a blank by putting its answer in {curly braces}.',
      );
    if (cloze.answers.some((a) => !a.text))
      publishProblems.push("A blank can't be empty.");
  }
  // Locked questions are held to this whatever their status.
  if (locked && isCloze && originalBlanks.length !== cloze.answers.length)
    lockProblems.push(
      "Students may already have answered this question, so blanks can't be added or removed.",
    );
  if (removedSpelling)
    lockProblems.push(
      "Students may already have answered this question, so its accepted answers can't be removed or changed. You can add other spellings.",
    );
  const problems = [...lockProblems, ...publishProblems];

  const formValues = JSON.stringify([
    type,
    text,
    clozeText,
    answers,
    score,
    order,
    status,
    hint,
    correctMsg,
    incorrectMsg,
    explanation,
    disableCorrect,
  ]);
  const [savedValues] = useState(formValues);
  const dirty = formValues !== savedValues;
  const {
    allowLeave,
    confirmDiscard,
    dialog: unsavedDialog,
  } = useUnsavedChanges(dirty);

  const invalidateLists = () =>
    queryClient.invalidateQueries({
      queryKey: api.adminListQuestions.getQueryKey({
        path: { lesson_id: lessonId },
      }),
    });

  const createMutation = api.adminCreateQuestion.useMutation(undefined, {
    meta: { toast: 'Created' },
    onSettled: () => {
      submitting.current = false;
    },
    onSuccess: (created) => {
      invalidateLists();
      allowLeave();
      // Replace, so Back returns to the list rather than to a blank form.
      navigate({
        to: '/questions/$questionId',
        params: { questionId: String(created.id) },
        replace: true,
      });
    },
  });

  const updateMutation = api.adminUpdateQuestion.useMutation(undefined, {
    onSettled: () => {
      submitting.current = false;
    },
    onSuccess: () => {
      invalidateLists();
      allowLeave();
      if (question) {
        queryClient.invalidateQueries({
          queryKey: api.adminGetQuestion.getQueryKey({
            path: { question_id: question.id },
          }),
        });
      }
      onDone();
    },
  });

  const deleteMutation = api.adminDeleteQuestion.useMutation(undefined, {
    onSuccess: () => {
      invalidateLists();
      allowLeave();
      navigate({
        to: '/questions',
        search: { lesson: lessonId, topic: topicId },
      });
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;
  const saveError = createMutation.error ?? updateMutation.error;

  // Options, blanks and "No correct answer" all survive a type change, so
  // picking the wrong type and switching back loses nothing.
  const changeType = (next: QuestionType) => {
    const correct = answers.filter((a) => a.is_correct).length;
    const trim = singleChoiceTrim.current;
    if (next === 'choose_one' && correct > 1) {
      const first = answers.findIndex((a) => a.is_correct);
      const after = answers.map((a, i) => ({ ...a, is_correct: i === first }));
      singleChoiceTrim.current = { before: answers, after };
      setAnswers(after);
    } else if (next === 'choose_multiple' && trim?.after === answers) {
      setAnswers(trim.before);
    }
    if (next === 'fill_in_blank' && !clozeText) setClozeText(text);
    if (type === 'fill_in_blank' && next !== 'fill_in_blank' && !text)
      setText(clozeText.replace(BLANK_RE, '______'));
    setType(next);
  };

  const updateAnswer = (index: number, patch: Partial<Answer>) =>
    setAnswers((prev) =>
      prev.map((a, i) => (i === index ? { ...a, ...patch } : a)),
    );

  const toggleCorrect = (index: number) =>
    setAnswers((prev) =>
      prev.map((a, i) => {
        if (i === index) return { ...a, is_correct: !a.is_correct };
        // Single choice has one right answer, so marking one clears the rest.
        return type === 'choose_one' ? { ...a, is_correct: false } : a;
      }),
    );

  const moveAnswer = (index: number, delta: number) =>
    setAnswers((prev) => {
      const next = [...prev];
      const [row] = next.splice(index, 1);
      next.splice(index + delta, 0, row);
      return next;
    });

  const toggleNoCorrect = (checked: boolean) => {
    setDisableCorrect(checked);
    if (checked)
      setAnswers((prev) => prev.map((a) => ({ ...a, is_correct: false })));
  };

  // New spellings on a locked blank are permanent, so they're confirmed first.
  const needsConfirm = addedSpellings.length > 0 && !removedSpelling;
  const spellingList = addedSpellings.map((s) => `"${s}"`).join(', ');

  const handleSave = () => {
    setTriedToSave(true);
    if (problems.length > 0 || submitting.current) return;
    if (needsConfirm) {
      setConfirming(true);
      return;
    }
    save();
  };

  const save = () => {
    setConfirming(false);
    if (submitting.current) return;
    submitting.current = true;
    const shared = {
      type,
      ...(isCloze
        ? { text: '', cloze_replacer: cloze.replacer }
        : { text: emptyToBlank(text) }),
      answers: submittedAnswers,
      score: Number(score),
      status,
      hint: emptyToBlank(hint),
      correct_msg: emptyToBlank(correctMsg),
      incorrect_msg: emptyToBlank(incorrectMsg),
      explanation: emptyToBlank(explanation),
      disable_correct: noCorrect,
    };
    if (question) {
      // A blank question's words are in `cloze_replacer`; don't send its empty text.
      const { text: _unused, ...withoutText } = shared;
      updateMutation.mutate({
        path: { question_id: question.id },
        body: {
          ...(isCloze ? withoutText : shared),
          order: Number(order),
          expected_revision: baseRevision,
        },
      });
    } else {
      createMutation.mutate({
        path: { lesson_id: lessonId },
        body: { ...shared, topic_id: topicId ?? null },
      });
    }
  };

  const handleDelete = () => {
    if (!question) return;
    if (!window.confirm(deletePrompt('question', question))) return;
    deleteMutation.mutate({ path: { question_id: question.id } });
  };

  const lockedSummary = isCloze
    ? "Students may already have answered this question, even offline in the LiteApp. It can't be deleted or returned to draft, blanks can't be added or removed, accepted answers can't be removed, and its points and order can't change. You can fix wording and add other accepted spellings."
    : "Students may already have answered this question, even offline in the LiteApp. It can't be deleted or returned to draft, and its options, points and order can't change. You can still fix wording.";

  return (
    <EditorLayout>
      <EditorMain>
        {locked && <InfoNotice>{lockedSummary}</InfoNotice>}
        {locked && isCloze && (
          <WarningNotice>
            New spellings only count for answers submitted from now on. Students
            who already answered keep the score they got, even if they typed a
            spelling you add.
          </WarningNotice>
        )}

        <FormSection
          tone="content"
          icon={faPen}
          title="Question"
          description="What students read and answer."
        >
          <FormRow>
            <FormLabel htmlFor="question-type">Type</FormLabel>
            <Select
              id="question-type"
              value={type}
              disabled={locked}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                changeType(e.target.value as QuestionType)
              }
            >
              {Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormRow>

          {isCloze ? (
            <FormRow>
              <FormLabel as="span">Question</FormLabel>
              <FieldHint>
                Put each answer in curly braces:{' '}
                <code>For all have {'{sinned}'}</code>. If more than one answer
                is right, put each in square brackets:{' '}
                <code>in the year {'{[63 AD][63]}'}</code>. Capital letters are
                ignored.
              </FieldHint>
              <RichTextEditor
                key={`cloze-${question?.id ?? 'new'}`}
                value={clozeText}
                onChange={setClozeText}
                ariaLabel="Question"
              />
              <Preview>
                <PreviewLabel>What students see</PreviewLabel>
                <RichContent
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: preview of the author's own HTML
                  dangerouslySetInnerHTML={{
                    __html: clozeHtml(cloze.replacer, [], 'gaps'),
                  }}
                />
              </Preview>
            </FormRow>
          ) : (
            <FormRow>
              <FormLabel as="span">Question</FormLabel>
              <RichTextEditor
                key={`text-${question?.id ?? 'new'}`}
                value={text}
                onChange={setText}
                ariaLabel="Question"
              />
            </FormRow>
          )}
        </FormSection>

        <FormSection
          tone="answers"
          icon={faListCheck}
          title={isChoiceType(type) ? 'Options' : 'Answers'}
          description={
            isCloze
              ? 'Read from the curly braces in the question.'
              : type === 'essay'
                ? undefined
                : noCorrect
                  ? 'This question has no correct answer.'
                  : type === 'choose_one'
                    ? 'Mark the one correct option.'
                    : 'Mark every correct option.'
          }
        >
          {isCloze && (
            <FormRow>
              <PreviewLabel>
                {cloze.answers.length}{' '}
                {cloze.answers.length === 1 ? 'blank' : 'blanks'}
              </PreviewLabel>
              {cloze.answers.length === 0 ? (
                <FieldHint>No blanks yet.</FieldHint>
              ) : (
                <BlankAnswers>
                  {cloze.answers.map((answer, i) => (
                    <li key={i}>
                      {answer.text ? (
                        acceptedSpellings(answer)
                      ) : (
                        <em>(empty)</em>
                      )}
                    </li>
                  ))}
                </BlankAnswers>
              )}
            </FormRow>
          )}

          {type === 'essay' && (
            <FieldHint>
              Students write their own answer. There's no correct answer: any
              answer gets full marks when submitted, and their study helper can
              comment on it.
            </FieldHint>
          )}

          {isChoiceType(type) && (
            <>
              <AnswerList>
                {answers.map((answer, i) => (
                  <AnswerRow key={i} $correct={answer.is_correct}>
                    <TextInput
                      aria-label={`Option ${i + 1}`}
                      placeholder={`Option ${i + 1}`}
                      value={answer.text}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        updateAnswer(i, { text: e.target.value })
                      }
                    />
                    {!disableCorrect && (
                      <CorrectToggle
                        aria-label={`Option ${i + 1} correct`}
                        aria-pressed={answer.is_correct}
                        $on={answer.is_correct}
                        disabled={locked}
                        onClick={() => toggleCorrect(i)}
                      >
                        {answer.is_correct ? '✓ Correct' : 'Mark correct'}
                      </CorrectToggle>
                    )}
                    <IconButton
                      aria-label={`Move option ${i + 1} up`}
                      disabled={locked || i === 0}
                      onClick={() => moveAnswer(i, -1)}
                    >
                      ↑
                    </IconButton>
                    <IconButton
                      aria-label={`Move option ${i + 1} down`}
                      disabled={locked || i === answers.length - 1}
                      onClick={() => moveAnswer(i, 1)}
                    >
                      ↓
                    </IconButton>
                    <IconButton
                      aria-label={`Remove option ${i + 1}`}
                      disabled={locked}
                      onClick={() =>
                        setAnswers((prev) => prev.filter((_, j) => j !== i))
                      }
                    >
                      ✕
                    </IconButton>
                  </AnswerRow>
                ))}
              </AnswerList>
              <InlineActions>
                <Button
                  className={buttonStyles.Button}
                  disabled={locked}
                  onClick={() =>
                    setAnswers((prev) => [
                      ...prev,
                      { text: '', is_correct: false },
                    ])
                  }
                >
                  Add option
                </Button>
                <CheckboxLabel $disabled={locked}>
                  <input
                    type="checkbox"
                    checked={disableCorrect}
                    disabled={locked}
                    onChange={(e) => toggleNoCorrect(e.target.checked)}
                  />
                  No correct answer
                </CheckboxLabel>
              </InlineActions>
              {locked &&
                !disableCorrect &&
                !answers.some((a) => a.is_correct) && (
                  <WarningNotice>
                    No option is marked correct and "No correct answer" isn't
                    ticked, so every answer to this question is marked wrong.
                    Students may already have it, so this can't be changed here.
                  </WarningNotice>
                )}
            </>
          )}
        </FormSection>

        <FormSection
          tone="feedback"
          icon={faComments}
          title="Feedback"
          description="Shown to students as they answer, and to study helpers while grading."
        >
          <FormRow>
            <FormLabel as="span">Hint</FormLabel>
            <RichTextEditor
              value={hint}
              onChange={setHint}
              ariaLabel="Hint"
              compact
            />
          </FormRow>
          <FormRow>
            <FormLabel as="span">Message when correct</FormLabel>
            <RichTextEditor
              value={correctMsg}
              onChange={setCorrectMsg}
              ariaLabel="Message when correct"
              compact
            />
          </FormRow>
          <FormRow>
            <FormLabel as="span">Message when incorrect</FormLabel>
            <RichTextEditor
              value={incorrectMsg}
              onChange={setIncorrectMsg}
              ariaLabel="Message when incorrect"
              compact
            />
          </FormRow>
          <FormRow>
            <FormLabel as="span">Explanation for study helpers</FormLabel>
            <RichTextEditor
              value={explanation}
              onChange={setExplanation}
              ariaLabel="Explanation for study helpers"
              compact
            />
          </FormRow>
        </FormSection>
      </EditorMain>

      <EditorPanel aria-label="Save and settings">
        <SaveGroup
          heading={<QuizBadge final={topicId === undefined} />}
          dirty={dirty}
          saving={saving}
          onSave={handleSave}
          onCancel={() => confirmDiscard(onDone)}
        >
          {triedToSave && problems.length > 0 && (
            <ErrorNotice role="alert">
              <ProblemTitle>
                {lockProblems.length > 0
                  ? "Can't save these changes:"
                  : 'To publish, fix these first (or save as a draft):'}
              </ProblemTitle>
              <ProblemList aria-label="Fix before saving">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ProblemList>
            </ErrorNotice>
          )}
          {needsConfirm && (
            <WarningNotice>
              Saving adds {spellingList} as{' '}
              {addedSpellings.length === 1
                ? 'an accepted answer'
                : 'accepted answers'}
              . Once saved, {addedSpellings.length === 1 ? 'it' : 'they'} can't
              be removed, and{' '}
              {addedSpellings.length === 1
                ? 'it only counts'
                : 'they only count'}{' '}
              for answers submitted from now on; earlier answers are not
              regraded.
            </WarningNotice>
          )}
          {saveError &&
            (editConflict(saveError) ? (
              <EditConflictNotice
                error={saveError}
                onReload={() => {
                  allowLeave();
                  if (question)
                    queryClient.invalidateQueries({
                      queryKey: api.adminGetQuestion.getQueryKey({
                        path: { question_id: question.id },
                      }),
                    });
                  onDone();
                }}
              />
            ) : (
              <ErrorText>
                {apiErrorDetail(saveError) ??
                  'Could not save the question. Please try again.'}
              </ErrorText>
            ))}
        </SaveGroup>

        <PanelGroup>
          <FormRow>
            <FormLabel htmlFor="question-status">Status</FormLabel>
            <Select
              id="question-status"
              value={status}
              disabled={locked && question?.status === 'published'}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setStatus(e.target.value as ContentStatus)
              }
            >
              <option value="draft">draft</option>
              <option value="published">published</option>
            </Select>
            {!quizLocked && (
              <FieldHint>
                Once students can reach this {parentName}, its published
                questions can only have their wording changed, and no questions
                can be added.
              </FieldHint>
            )}
          </FormRow>
          <FieldPair>
            <FormRow>
              <FormLabel htmlFor="question-score">Points</FormLabel>
              <TextInput
                id="question-score"
                type="number"
                min={1}
                value={score}
                disabled={locked}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setScore(e.target.value)
                }
              />
            </FormRow>
            {!creating && (
              <FormRow>
                <FormLabel htmlFor="question-order">Order</FormLabel>
                <TextInput
                  id="question-order"
                  type="number"
                  min={0}
                  value={order}
                  disabled={locked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOrder(e.target.value)
                  }
                />
              </FormRow>
            )}
          </FieldPair>
        </PanelGroup>

        {!creating && (
          <DangerGroup>
            <Button
              className={`${buttonStyles.Button} ${buttonStyles.Danger}`}
              onClick={handleDelete}
              disabled={deleteMutation.isPending || locked}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete question'}
            </Button>
            {locked && (
              <FieldHint>
                Students may have access to this question, so it can't be
                deleted.
              </FieldHint>
            )}
            {deleteMutation.isError && (
              <ErrorText>
                {apiErrorDetail(deleteMutation.error) ??
                  'Could not delete the question. Please try again.'}
              </ErrorText>
            )}
          </DangerGroup>
        )}
      </EditorPanel>

      <ConfirmDialog
        open={confirming}
        title="This can't be undone"
        confirmLabel="Save anyway"
        onConfirm={save}
        onCancel={() => setConfirming(false)}
      >
        <p>
          Saving adds {spellingList} as{' '}
          {addedSpellings.length === 1
            ? 'an accepted answer'
            : 'accepted answers'}
          . Once saved, {addedSpellings.length === 1 ? 'it' : 'they'} can't be
          removed.
        </p>
        <p>
          {addedSpellings.length === 1 ? 'It only counts' : 'They only count'}{' '}
          for answers submitted from now on; earlier answers keep the score they
          got.
        </p>
      </ConfirmDialog>
      {unsavedDialog}
    </EditorLayout>
  );
}

const Preview = styled.div`
  background: var(--grey-75);
  border: 1px dashed var(--grey-450);
  border-radius: 0.375rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
`;

const PreviewLabel = styled.span`
  color: var(--grey-600);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const BlankAnswers = styled.ol`
  font-size: 0.875rem;
  margin: 0;
  padding-left: 1.5rem;
`;

const AnswerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const AnswerRow = styled.div<{ $correct: boolean }>`
  align-items: center;
  border-left: 3px solid
    ${(p) => (p.$correct ? 'var(--tone-answers)' : 'transparent')};
  display: flex;
  gap: 0.5rem;
  padding-left: 0.5rem;
`;

const CorrectToggle = styled(Button)<{ $on: boolean }>`
  background: ${(p) => (p.$on ? 'var(--tone-answers)' : 'transparent')};
  border: 1px solid
    ${(p) => (p.$on ? 'var(--tone-answers)' : 'var(--grey-450)')};
  border-radius: 999px;
  color: ${(p) => (p.$on ? 'white' : 'var(--grey-700)')};
  cursor: pointer;
  flex-shrink: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  min-width: 7.5rem;
  padding: 0.375rem 0.75rem;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const IconButton = styled(Button)`
  background: none;
  border: 1px solid var(--grey-450);
  border-radius: 0.375rem;
  color: var(--grey-700);
  cursor: pointer;
  flex-shrink: 0;
  height: 2rem;
  width: 2rem;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const InlineActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ProblemTitle = styled.strong`
  display: block;
`;

const ProblemList = styled.ul`
  margin: 0.25rem 0 0;
  padding-left: 1.25rem;
`;
